import Storefront from '../utils/storefront';
import { getProductsByIds } from '../utils/storefront/queries';
import { setProductCartLabel } from '../utils/helpers';
import CartService from './cart-service';

/**
 * Cart label refresh functionality
 * Handles refreshing _cart_label properties when product tags/pricing changes
 * Compatible with both Ajax and Storefront API modes
 */
class CartLabel {
  constructor() {
    this.lastRefresh = 0;
    this.refreshCooldown = 5 * 60 * 1000; // 5 minutes
    this.sessionRefreshKey = 'cart_labels_refreshed_session';
    this.storefront = new Storefront();
  }

  /**
   * Determines if cart labels should be refreshed based on trigger and cooldown
   * @param {string} trigger - The trigger for refresh ('checkout', 'minicart', 'cartpage')
   * @returns {boolean} Whether refresh should proceed
   */
  shouldRefresh(trigger) {
    const now = Date.now();

    // Always refresh on checkout (critical path)
    if (trigger === 'checkout') return true;

    // Check cooldown for other triggers
    if (now - this.lastRefresh < this.refreshCooldown) {
      return false;
    }

    // Check if already refreshed this session for non-critical triggers
    if (
      trigger !== 'checkout' &&
      sessionStorage.getItem(this.sessionRefreshKey)
    ) {
      return false;
    }

    return true;
  }

  /**
   * Gets cart items from cart object, handling both Ajax and Storefront modes
   * @param {Object} cart - Cart object
   * @returns {Array} Array of cart items
   */
  getCartItems(cart) {
    if (!cart) return [];

    // Ajax mode uses 'items', Storefront mode uses 'lines'
    if (cart.items && Array.isArray(cart.items)) {
      return cart.items;
    }

    if (cart.lines && Array.isArray(cart.lines)) {
      return cart.lines;
    }

    // Fallback: check nested state object
    if (cart.state) {
      if (cart.state.items && Array.isArray(cart.state.items)) {
        return cart.state.items;
      }
      if (cart.state.lines && Array.isArray(cart.state.lines)) {
        return cart.state.lines;
      }
    }

    return [];
  }

  /**
   * Extracts product ID from cart item, handling different formats
   * @param {Object} item - Cart item
   * @returns {string|null} Product ID
   */
  getProductId(item) {
    if (!item) return null;

    // Ajax mode: direct product_id
    if (item.product_id) {
      return item.product_id.toString();
    }

    // Storefront mode: extract from merchandise.product.id
    if (item.merchandise?.product?.id) {
      return item.merchandise.product.id.split('/').pop();
    }

    // Alternative storefront format
    if (item.product?.id) {
      return item.product.id.split('/').pop();
    }

    return null;
  }

  /**
   * Gets the cart line key/id for updates, handling different formats
   * @param {Object} item - Cart item
   * @returns {string|null} Line key/id for updates
   */
  getLineKey(item) {
    if (!item) return null;

    // Ajax mode: use key or id
    if (item.key) return item.key;
    if (item.id) return item.id;

    // Storefront mode: use id
    if (item.id) return item.id;

    return null;
  }

  /**
   * Checks if any cart items have labels that need re-validation
   * @param {Object} cart - Cart object with items
   * @returns {boolean} Whether any items need label refresh
   */
  hasItemsNeedingRefresh(cart) {
    const items = this.getCartItems(cart);
    if (!items || items.length === 0) return false;

    // Check for ANY items that have _cart_label property (as per original requirements)
    const itemsWithLabels = items.filter(
      (item) => item.properties?._cart_label !== undefined,
    );

    return itemsWithLabels.length > 0;
  }

  /**
   * Fetches current product data for given product IDs
   * @param {Array} productIds - Array of product IDs
   * @returns {Promise<Object>} Map of product ID to tags
   */
  async fetchProductTags(productIds) {
    try {
      // Format product IDs for Storefront API
      const formattedIds = productIds.map((id) => {
        if (id.startsWith('gid://shopify/Product/')) {
          return id;
        }
        return `gid://shopify/Product/${id}`;
      });

      const response = await this.storefront.request({
        query: getProductsByIds,
        variables: { ids: formattedIds },
      });

      if (!response.data || !response.data.nodes) {
        throw new Error(
          'Failed to fetch product data - no data or nodes in response',
        );
      }

      // Create a map of product ID to current tags
      const productTagsMap = {};

      // Filter out null products and process valid ones
      const validProducts = response.data.nodes.filter(
        (product) => product !== null && product !== undefined && product.id,
      );

      validProducts.forEach((product) => {
        if (product && product.id) {
          const productId = product.id.split('/').pop();
          productTagsMap[productId] = product.tags || [];
        }
      });

      return productTagsMap;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Cart label refresh: Error fetching product tags:', error);
      return this.getFallbackProductTags(productIds);
    }
  }

  /**
   * Fallback method to get product tags from existing cart/page data
   * @param {Array} productIds - Array of product IDs
   * @returns {Object} Map of product ID to tags (may be empty)
   */
  getFallbackProductTags(productIds) {
    const productTagsMap = {};

    // Try to get product data from global cart context if available
    if (window.cartProductsData) {
      productIds.forEach((id) => {
        const productData = window.cartProductsData[id];
        if (productData && productData.tags) {
          productTagsMap[id] = productData.tags;
        }
      });
    }

    // Try to get product data from page context if available
    if (Object.keys(productTagsMap).length === 0 && window.productData) {
      productIds.forEach((id) => {
        if (window.productData.id && window.productData.id.toString() === id) {
          productTagsMap[id] = window.productData.tags || [];
        }
      });
    }

    // If still no data, create empty arrays to prevent further errors
    if (Object.keys(productTagsMap).length === 0) {
      productIds.forEach((id) => {
        productTagsMap[id] = [];
      });
    }

    return productTagsMap;
  }

  /**
   * Identifies cart items that need label updates
   * @param {Object} cart - Cart object with items
   * @param {Object} productTagsMap - Map of product ID to current tags
   * @returns {Array} Array of items that need updates
   */
  identifyItemsNeedingUpdate(cart, productTagsMap) {
    const items = this.getCartItems(cart);
    const itemsToUpdate = [];

    // eslint-disable-next-line no-console
    console.log('Cart label refresh: Identifying items needing label updates');

    items.forEach((item) => {
      const productId = this.getProductId(item);
      const lineKey = this.getLineKey(item);

      if (!productId || !lineKey) return;

      const currentTags = productTagsMap[productId];
      if (!currentTags) return;

      // Generate new bilingual label based on current tags
      const newLabel = setProductCartLabel(currentTags);
      const currentLabel = item.properties?._cart_label;

      // Skip update if both labels are null/empty
      if (!newLabel && !currentLabel) return;

      // If either label is null/undefined, they're different
      if (!newLabel || !currentLabel) {
        // eslint-disable-next-line no-console
        console.log(
          `Cart label refresh: Item ${lineKey} needs update - missing`,
        );
        itemsToUpdate.push({
          id: lineKey,
          attributes: [
            {
              key: '_cart_label',
              value: newLabel || '',
            },
          ],
        });
        return;
      }

      // Normalize labels for comparison (trim whitespace, normalize commas)
      // Trim and normalize commas for both labels
      const trimmed = currentLabel.trim();
      // eslint-disable-next-line max-len
      const normalizedCurrentLabel = trimmed.replace(/\s*,\s*/g, ',');
      // eslint-disable-next-line max-len
      const normalizedNewLabel = newLabel.trim().replace(/\s*,\s*/g, ',');

      // Check if labels are different after normalization
      const labelsAreDifferent = normalizedNewLabel !== normalizedCurrentLabel;

      if (labelsAreDifferent) {
        // eslint-disable-next-line no-console
        console.log(`Cart label refresh: Item ${lineKey} needs update`);
        // eslint-disable-next-line no-console
        console.log(`  Current: "${normalizedCurrentLabel}"`);
        // eslint-disable-next-line no-console
        console.log(`  New: "${normalizedNewLabel}"`);

        itemsToUpdate.push({
          id: lineKey,
          attributes: [
            {
              key: '_cart_label',
              value: newLabel,
            },
          ],
        });
      }
    });

    return itemsToUpdate;
  }

  /**
   * Updates cart items with new labels
   * @param {Array} itemsToUpdate - Array of items to update
   * @returns {Promise<boolean>} Whether updates were successful
   */
  async updateCartItemLabels(itemsToUpdate) {
    try {
      // Track successful updates
      let updateCount = 0;
      const totalUpdates = itemsToUpdate.length;

      // Log update attempt for debugging
      // eslint-disable-next-line no-console
      console.log(
        `Cart label refresh: Updating ${totalUpdates} items with new labels`,
      );

      // Ajax mode only supports updating one item at a time
      // Update items sequentially to avoid conflicts
      // eslint-disable-next-line no-restricted-syntax
      for (const itemUpdate of itemsToUpdate) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await CartService.updateAttributes(itemUpdate, true); // silent update
          updateCount += 1;

          // Log progress for debugging
          // eslint-disable-next-line no-console
          console.log(
            `Cart label refresh: Updated item ${updateCount}/${totalUpdates}`,
          );
        } catch (itemError) {
          // eslint-disable-next-line no-console
          console.error(
            `Cart label refresh: Failed to update item ${itemUpdate.id}:`,
            itemError,
          );
          // Continue with other items even if one fails
        }
      }

      // Only proceed with UI updates if at least one item was updated successfully
      if (updateCount > 0) {
        // Refresh cart data after all updates - use non-silent to trigger UI updates
        const updatedCart = await CartService.get(false);

        if (!updatedCart) {
          throw new Error('Failed to refresh cart data after label updates');
        }

        // Force cart context refresh through multiple mechanisms to ensure UI updates

        // 1. Dispatch cart:updated event for components listening to this event
        document.dispatchEvent(new CustomEvent('cart:updated'));

        // 2. Notify CartService listeners directly
        if (window.CartService && window.CartService.notifyListeners) {
          window.CartService.notifyListeners('UPDATE');
        }

        // 3. Dispatch additional events that might trigger UI updates
        document.dispatchEvent(new CustomEvent('cart:refresh'));

        // 4. If mini-cart is open, trigger a refresh
        const miniCart = document.querySelector(
          '.mini-cart:not(.translate-x-full)',
        );
        if (miniCart) {
          document.dispatchEvent(new CustomEvent('openMiniCart'));
        }

        // Longer delay to ensure cart context has time to update
        await new Promise((resolve) => {
          setTimeout(resolve, 250);
        });

        // eslint-disable-next-line no-console
        console.log(
          `Cart label refresh: Updated ${updateCount}/${totalUpdates} items`,
        );
      } else {
        // eslint-disable-next-line no-console
        console.warn('Cart label refresh: No items were successfully updated');
      }

      return updateCount > 0;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        'Cart label refresh: Error updating cart item labels:',
        error,
      );

      // Try one more time to refresh the cart to ensure UI is in sync
      try {
        await CartService.get(false);
      } catch (refreshError) {
        // Ignore errors from this final attempt
      }

      return false;
    }
  }

  /**
   * Main method to refresh cart labels
   * @param {string} trigger - The trigger for refresh ('checkout', 'minicart', 'cartpage')
   * @returns {Promise<boolean>} Whether refresh was performed
   */
  async refresh(trigger = 'manual') {
    // Check if refresh should proceed
    if (!this.shouldRefresh(trigger)) {
      return false;
    }

    // eslint-disable-next-line no-console
    console.log(`Cart label refresh: Starting refresh (trigger: ${trigger})`);

    try {
      // Get current cart data
      const currentCart = await CartService.get(true); // silent get
      const items = this.getCartItems(currentCart);

      if (!items || items.length === 0) {
        // eslint-disable-next-line no-console
        console.log('Cart label refresh: No items in cart, skipping refresh');
        return false;
      }

      // Check if any items actually need refresh
      if (!this.hasItemsNeedingRefresh(currentCart)) {
        // eslint-disable-next-line no-console
        console.log(
          'Cart label refresh: No items with labels found, skipping refresh',
        );
        return false;
      }

      // Extract unique product IDs from cart items
      const productIds = [
        ...new Set(
          items
            .map((item) => this.getProductId(item))
            .filter((id) => id !== null),
        ),
      ];

      if (productIds.length === 0) {
        // eslint-disable-next-line no-console
        console.log(
          'Cart label refresh: No valid product IDs found, skipping refresh',
        );
        return false;
      }

      // eslint-disable-next-line no-console
      console.log(
        `Cart label refresh: Fetching data for ${productIds.length} products`,
      );

      // Fetch current product data
      const productTagsMap = await this.fetchProductTags(productIds);
      if (Object.keys(productTagsMap).length === 0) {
        // eslint-disable-next-line no-console
        console.log(
          'Cart label refresh: No product data retrieved, skipping refresh',
        );
        return false;
      }

      // Identify items that need updates
      const itemsToUpdate = this.identifyItemsNeedingUpdate(
        currentCart,
        productTagsMap,
      );

      // Update items if needed
      if (itemsToUpdate.length > 0) {
        // eslint-disable-next-line no-console
        console.log(
          `Cart label refresh: Found ${itemsToUpdate.length} items to update`,
        );

        const success = await this.updateCartItemLabels(itemsToUpdate);
        if (!success) {
          // eslint-disable-next-line no-console
          console.error(
            'Cart label refresh: Failed to update cart item labels',
          );
          return false;
        }
      } else {
        // eslint-disable-next-line no-console
        console.log('Cart label refresh: No items need label updates');
      }

      // Update refresh tracking
      this.lastRefresh = Date.now();
      if (trigger !== 'checkout') {
        sessionStorage.setItem(this.sessionRefreshKey, 'true');
      }

      // eslint-disable-next-line no-console
      console.log(`Cart label refresh: Refresh complete (trigger: ${trigger})`);
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Cart label refresh: Error refreshing cart labels:', error);
      return false;
    }
  }

  /**
   * Clears the session refresh flag (useful for testing or manual resets)
   */
  clearSessionFlag() {
    sessionStorage.removeItem(this.sessionRefreshKey);
  }

  /**
   * Forces a refresh regardless of cooldown (for testing/debugging)
   * @param {string} trigger - The trigger for refresh
   * @returns {Promise<boolean>} Whether refresh was performed
   */
  async forceRefresh(trigger = 'manual') {
    this.lastRefresh = 0;
    this.clearSessionFlag();
    return this.refresh(trigger);
  }
}

// Export singleton instance
const CartLabelSingleton = new CartLabel();

// Make it globally available for cart page access and debugging
if (typeof window !== 'undefined') {
  window.CartLabelSingleton = CartLabelSingleton;
}

export default CartLabelSingleton;
